import logoPrimary from '@assets/images/common/brand/logo-primary.svg';
import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { BizsolUserInfo } from '@src/api/company/auth/bizsol/bizsolApiClient';
import { companyLoginApiClient, LoginRequest } from '@src/api/company/auth/companyLoginApiClient';
import { companySiteApiClient } from '@src/api/company/site/companySiteApiClient';
import { fetchCompanyUserProfile } from '@src/context/auth/ProfileFetcher';
import { useCompanyAuth } from '@src/context/auth/useCompanyAuth';
import { TogglePassword } from '@src/features/company/auth/components/TogglePassword';
import { useToast } from '@src/features/toast/context/toastContext';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { COMPANY_URL } from '@src/routes/company';
import { handlePostLoginRedirect } from '@src/utils/redirectAfterLogin';
import { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

function LoginForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<LoginRequest>();

  const [generalError, setGeneralError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const [isRevealPassword, setIsRevealPassword] = useState(false);
  const companyAuth = useCompanyAuth();
  const toast = useToast();

  useEffect(() => {
    if (errors.email || generalError) {
      if (emailRef.current) {
        emailRef.current.style.backgroundColor = 'rgba(251,233,238,0.7)';
        emailRef.current.style.borderColor = '#E0245E';
      }
    }
    if (errors.password || generalError) {
      if (passwordRef.current) {
        passwordRef.current.style.backgroundColor = 'rgba(251,233,238,0.7)';
        passwordRef.current.style.borderColor = '#E0245E';
      }
    }
  }, [errors, generalError]);

  const onSubmit: SubmitHandler<LoginRequest> = async (params) => {
    clearErrors();
    setGeneralError(null);
    let hasBizsolSub = false;
    if (sessionStorage.getItem('bizsolUserInfo')) {
      // sessionStorage.getItem('bizsolUserInfo')をBizsolUserInfoとして取得
      const bizsolUserInfo: BizsolUserInfo = JSON.parse(
        sessionStorage.getItem('bizsolUserInfo') || '{}',
      );
      if (bizsolUserInfo.sub) {
        params.bizsol_sub = bizsolUserInfo.sub;
        hasBizsolSub = true;
      }
    }
    const res = await companyLoginApiClient.login(params);
    handleApiResponse(
      res,
      async (data) => {
        // ログイン成功時のみbizsolUserInfoを削除
        if (hasBizsolSub) {
          sessionStorage.removeItem('bizsolUserInfo');
        }
        if (data.two_factor_enabled) {
          toast(TOAST_MESSAGE.SEND);
          navigate(COMPANY_URL.TWO_FACTOR, {
            state: {
              email: params.email,
              password: params.password,
              isFirstLogin: data.is_first_login,
              session: data.session,
            },
          });
          return;
        }

        if (data.is_first_login) {
          navigate(COMPANY_URL.PASSWORD_EDIT, {
            state: { email: params.email },
          });
          return;
        }

        const res = await companySiteApiClient.topPageUrl();
        handleApiResponse(res, (data) => {
          fetchCompanyUserProfile(companyAuth.setProfile);

          if (handlePostLoginRedirect(navigate, searchParams)) {
            return;
          }

          navigate(data.url);
        });
      },
      (errors) => {
        setGeneralError(
          errors.messages?.[0] ||
            'ログインに失敗しました。入力内容に誤りがないか確認してください。',
        );
      },
    );
  };

  const togglePassword = () => {
    setIsRevealPassword((prevState) => !prevState);
  };

  return (
    <div className='flex flex-col gap-8'>
      <div className='grid place-items-center'>
        <img
          alt={import.meta.env.VITE_APP_NAME}
          className='h-auto max-h-[72px] w-[400px] object-contain'
          src={logoPrimary}
        />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-col gap-[6px]'>
            <label
              className='label-login'
              htmlFor='email'
            >
              メールアドレス
            </label>
            <input
              id='email'
              className={`input-login ${errors.email || generalError ? 'error-input' : ''}`}
              placeholder='メールアドレス'
              {...register('email')}
            />
          </div>
          <div className='flex flex-col gap-[6px]'>
            <label
              className='label-login'
              htmlFor='password'
            >
              パスワード
            </label>
            <div className='relative'>
              <input
                id='password'
                type={isRevealPassword ? 'text' : 'password'}
                className={`input-login ${errors.password || generalError ? 'error-input' : ''}`}
                placeholder='パスワード'
                {...register('password')}
              />
              <button
                type='button'
                onClick={togglePassword}
                className='toggle-password'
              >
                <TogglePassword isReveal={isRevealPassword} />
              </button>
            </div>
          </div>
        </div>
        {generalError && <div className='error-message'>{generalError}</div>}
        <div className='mt-4 flex justify-center'>
          <Link
            className='text-sm text-primary underline hover:no-underline'
            to={COMPANY_URL.PASSWORD_RESET_SENDMAIL}
          >
            パスワードを忘れた場合
          </Link>
        </div>
        <button
          className='btn-login mt-6'
          type='submit'
        >
          ログイン
        </button>
        <div className='mt-4 flex justify-center'>
          <Link
            className='text-sm text-primary underline hover:no-underline'
            to={COMPANY_URL.REGISTRATION}
          >
            会員登録（無料）はこちら
          </Link>
        </div>
      </form>
    </div>
  );
}

export default LoginForm;
